import * as React from "react";
import { merge, $, media } from "glamor";
import { theme } from "styles/theme";
import { formatMessage } from "lib/globalize-util";
import Dialog from "components/dialog/dialog";
import Button from "components/form/button";
import type { IScope } from "reducers/scope";
import Toggle from "material-ui/Toggle";
import {
    Checkbox,
    ListItemText,
    FormControl,
    FormLabel,
    Select,
    MenuItem,
    Grid,
    TextField,
    Button as MUIButton,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import siteConfig from "site-config";
import { fetch } from "lib/fetch";
import { IProduct } from "records/item/item";
import { trimKeyPrefix, keyBy, getUrlForNoSidePanel, checkMultiTypeKeyExists } from "./helpers";
import Kabob from "./product-edit-kabob";
import moment from "moment";
import { Add } from "@material-ui/icons";
import { withRouter } from "react-router";
import { v4 as uuid } from "uuid";
import SizeRow from "./size-row";
import SizePacks from "./size-packs";
import { cloneDeep, escapeRegExp, has } from "lodash";
import { createMarkup } from "lib/helpers";

export const FIELD_PREFIX = "__";

export enum ProductField {
    Id = "_id",
    TaxExempt = "__exempt_from_order_surcharge",
    Active = "__active",
    StyleNumber = "style_number",
    Season = "season",
    Color = "color",
    Name = "name",
    Description = "description",
    AvailableFrom = "__available_from",
    AvailableTo = "__available_until",
    Pricing = "__pricing",
    Sizes = "__sizes",
    PriceTemplates = "__price_templates",
    Banners = "__banners",
    sizePacks = "__pack_ids",
}

const EDITABLE_FIELDS = [
    ProductField.TaxExempt,
    ProductField.Active,
    ProductField.StyleNumber,
    ProductField.Season,
    ProductField.Color,
    ProductField.Name,
    ProductField.Description,
    ProductField.AvailableFrom,
    ProductField.AvailableTo,
    ProductField.Pricing,
    ProductField.Sizes,
    ProductField.Banners,
];

export enum PricingField {
    Disabled = "__disabled",
    Wholesale = "__wholesale",
    Retail = "__retail",
}

export enum SizesField {
    Size = "__size",
    UnitsPerPack = "__units_per_pack",
    ShippingWeight = "__weight",
}

export enum ItemTypes {
    SizeRow = "sizeRow",
}

export interface DragItem {
    index: number;
    id: string;
    type: ItemTypes;
}

interface IUpdateOptions {
    isMulti?: boolean;
}

interface ISchemaField {
    key: string;
    details: any;
}

interface ICurrency {
    currency_code: string;
    wholesale: number;
    retail: number;
    disabled: boolean;
}

interface IProps {
    item: IProduct;
    onCloseModal: () => void;
    scope: IScope;
    onSnackbarAdd: (params: any) => void;
    onEntriesRefresh: () => void;
    schemas: any;
    router?: any;
}

// Units per Pack: not required positive integer
export const isUnitsPerPackInvalid = (value) => value !== undefined && (!Number.isInteger(+value) || +value < 1);

// Shipping Weight: not required number >= 0
export const isShippingWeightInvalid = (value) => value !== undefined && (isNaN(+value) || +value < 0);

const BASE_FIELDS = [
    ProductField.StyleNumber,
    ProductField.Season,
    ProductField.Color,
    ProductField.Name,
    ProductField.Description,
];
const REQUIRED_FIELDS = [ProductField.StyleNumber, ProductField.Season, ProductField.Color, ProductField.Name];

const ProductEdit: React.FC<IProps> = (props) => {
    const { item, onCloseModal, scope, onSnackbarAdd, onEntriesRefresh, schemas, router } = props;
    const [product, setProduct] = React.useState<any>(null);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [errorWarning, setErrorWarning] = React.useState<string>("");
    const [errorResponseObj, setErrorResponseObj] = React.useState<any>({});

    const styles = React.useMemo(() => {
        return merge(
            {
                width: "auto",
            },
            $(" .MuiFormControl-root.dateField .MuiFormLabel-root", {
                transform: "translate(14px, -4px) scale(0.92)",
            }),
            $(" .productDescription.MuiFormControl-root", {
                width: "100%",
            }),
            $(" .MuiFormControl-root textarea", {
                minHeight: "32px",
            }),
            $(" .MuiFormLabel-root", {
                color: theme.palette.textColor,
            }),
            $(" .MuiInputLabel-outlined.MuiInputLabel-shrink", {
                transform: "translate(14px, -4px) scale(0.92)",
            }),
            $(" .MuiAlert-message", {
                fontSize: "1.4rem",
            }),
            $(" .MuiInputLabel-formControl", {
                top: "-8px",
            }),
            $(" .customLabel.MuiFormLabel-root", {
                position: "absolute",
                fontSize: "0.84rem",
                top: -12,
            }),
            $(" .MuiOutlinedInput-input", {
                padding: "9.5px 9px",
            }),
            $(" .requiredLabel", {
                marginTop: "-32px",
                marginBottom: "24px",
            }),
            $(" .sizesRow", {
                display: "flex",
                alignItems: "center",
            }),
            $(" .dragHandler, .sizesDelete", {
                display: "flex",
                alignItems: "center",
                padding: "4px",
            }),
            $(" .sizesDelete", {
                cursor: "pointer",
            }),
            $(" .addSize button", {
                textDecoration: "underline",
            }),
            $(" .addSize .MuiSvgIcon-root", {
                fontSize: "2rem",
                marginRight: "8px",
                fill: theme.palette.primary1Color,
            }),
            $(" h3", {
                marginBottom: "24px",
                color: theme.palette.textColor,
            }),
            media(
                "(min-width: 600px)",
                $(" .MuiFormControl-root", {
                    width: "calc(50% - 16px)",
                    margin: "16px 8px",
                }),
                $(" .MuiFormControl-root.pricingToggle", {
                    width: "100%",
                })
            ),
            media(
                "(min-width: 1200px)",
                $(" .MuiFormControl-root", {
                    width: "calc(20% - 16px)",
                    margin: "16px 8px",
                }),
                $(" .MuiFormControl-root.pricingToggle", {
                    maxWidth: "120px",
                })
            )
        );
    }, []);

    const modalStyles = React.useMemo(
        () => ({
            body: {
                paddingTop: "42px",
            },
            contentStyle: {
                width: "96%",
                maxWidth: "none",
            },
            actionsContainerStyle: {
                display: "flex",
                justifyContent: "flex-end",
            },
            btnSave: {
                marginLeft: "10px",
            },
            selectMenu: {
                zIndex: 2000,
            },
        }),
        []
    );

    const schemaFields = React.useMemo(() => {
        const fields = {
            key_fields: [],
            long_fields: [],
            short_fields: [],
            size_fields: [],
            date_fields: [],
            all_fields: [],
        };
        const productSchema = schemas?.[item?.__schema_id];
        if (productSchema) {
            const schema = productSchema.toJS();
            fields.all_fields = schema.fields;
            schema.fields
                .filter((field) => !BASE_FIELDS.includes(field.key))
                .forEach((field: ISchemaField) => {
                    const key = field.key;
                    const subtype = field.details.subtype;

                    if (schema.composite_key_fields.indexOf(key) !== -1) {
                        fields.key_fields.push(field);
                    } else if (key.split(".").length > 1) {
                        fields.size_fields.push(field);
                    } else if (subtype === "date") {
                        fields.date_fields.push(field);
                    } else if (subtype === "long string") {
                        fields.long_fields.push(field);
                    } else {
                        fields.short_fields.push(field);
                    }
                });
        }

        return fields;
    }, [schemas, item]);

    const requiredFields = React.useMemo(
        () =>
            REQUIRED_FIELDS.concat(
                schemaFields.all_fields
                    .filter((field) => field.required && field.key.split(".").length < 2)
                    .map((field) => field.key)
            ),
        [schemaFields]
    );

    // if any size pricing differs from default pricing, treat as pps
    const productIsPricePerSize = React.useMemo(() => {
        if (!product) {
            return false;
        }
        const priceMap = keyBy(product.__pricing, "__currency_code");
        const { __sizes = [] } = product;

        return __sizes.some(({ __pricing = [] }) =>
            __pricing.some(({ __currency_code, __wholesale, __retail }) => {
                const defaultPrice = priceMap[__currency_code] || {};
                return __wholesale !== defaultPrice.__wholesale || __retail !== defaultPrice.__retail;
            })
        );
    }, [product]);

    const currencies: ICurrency[] = React.useMemo(() => {
        if (!product) {
            return [];
        }
        const currenciesList = scope.brand?.settings?.currencies ?? [];
        return currenciesList.map((currency: string) => {
            const currencyInfo: Partial<ICurrency> = {
                currency_code: currency,
            };

            (product.__pricing ?? []).forEach((item) => {
                if (item.__currency_code == currency) {
                    currencyInfo.wholesale = item.__wholesale;
                    currencyInfo.retail = item.__retail;
                    currencyInfo.disabled = item.__disabled;
                }
            });
            return currencyInfo;
        });
    }, [scope, product]);

    const handleCloseSidePanel = React.useCallback(() => {
        const { item = "" } = router.params;
        const newUrl = getUrlForNoSidePanel(item, router.location);
        router.push(newUrl);
    }, [getUrlForNoSidePanel]); // eslint-disable-line react-hooks/exhaustive-deps

    const banners = React.useMemo(() => scope.brand?.modules?.base?.product_banners ?? [], [scope]);

    const handleSave = React.useCallback(() => {
        setErrorWarning("");
        setErrorResponseObj({});
        setIsSaving(true);
        const sizeFields = product[ProductField.Sizes] ?? [];
        if (
            requiredFields.some((field) => !product[field]) ||
            sizeFields.some((size) => {
                let sizeValue;
                if (size.__size_group) {
                    sizeValue = size.__size.replace(new RegExp(escapeRegExp(size.__size_group) + "$"), "");
                } else {
                    sizeValue = size.__size;
                }
                return !sizeValue;
            })
        ) {
            setErrorWarning(formatMessage("Missing required fields"));
            setIsSaving(false);
            return;
        }
        if (!productIsPricePerSize) {
            const pricingFields = product[ProductField.Pricing] ?? [];
            if (pricingFields.some((field) => isNaN(+field.__retail) || isNaN(+field.__wholesale))) {
                setErrorWarning(formatMessage("Invalid pricing value."));
                setIsSaving(false);
                return;
            }
        }
        const isSizeFieldInvalid = [
            {
                fieldName: "__units_per_pack",
                validator: isUnitsPerPackInvalid,
                message: "Invalid Units per Pack Value.",
            },
            {
                fieldName: "__weight",
                validator: isShippingWeightInvalid,
                message: "Invalid Shipping Weight.",
            },
        ].some(({ fieldName, validator, message }) => {
            if (sizeFields.some((size) => validator(size[fieldName]))) {
                setErrorWarning(formatMessage(message));
                setIsSaving(false);
                return true;
            }
            return false;
        });
        if (isSizeFieldInvalid) {
            return;
        }
        const productFields = EDITABLE_FIELDS.concat(schemaFields.all_fields.map((field) => field.key));
        const productObj = cloneDeep(product);
        const _productData = {};
        productFields.forEach((field) => {
            _productData[field] = productObj[field];
        });
        const productData = trimKeyPrefix(_productData, FIELD_PREFIX, [
            ProductField.TaxExempt,
            SizesField.UnitsPerPack,
            SizesField.ShippingWeight,
        ]);
        fetch(`${siteConfig.api}/product/adminupdate/${product._id}`, {
            method: "POST",
            body: JSON.stringify(productData),
            noDomain: true,
        })
            .then((response) => {
                if (response.status === 409) {
                    setIsSaving(false);
                    setErrorWarning(
                        formatMessage(
                            "A product already exists with the same season, style number, and color or external brand_id."
                        )
                    );
                } else if (response.status > 300) {
                    return response.json().then((data) => {
                        setErrorResponseObj(data?.err?.errors || {});
                        throw new Error(data.message || formatMessage("Bad response from server"));
                    });
                } else {
                    setIsSaving(false);
                    onSnackbarAdd({ message: formatMessage("Your product has been updated.") });
                    onEntriesRefresh();
                    handleCloseSidePanel();
                    process.nextTick(() => onCloseModal());
                }
                return response;
            })
            .catch((e) => {
                setIsSaving(false);
                onSnackbarAdd({ message: e.message });
            });
    }, [product, onCloseModal, schemaFields, requiredFields, productIsPricePerSize]); // eslint-disable-line react-hooks/exhaustive-deps

    const updateProduct = React.useCallback(
        (fields: string[], values: (string | boolean | string[])[]) => {
            const newData = {};
            const dateFields = keyBy(schemaFields.date_fields, "key");
            fields.forEach((field, idx) => {
                if (dateFields[field]) {
                    // convert string values of date fields to timestamp
                    newData[field] = moment.utc(values[idx] as string).valueOf();
                } else {
                    newData[field] = values[idx];
                }
            });
            setProduct({ ...product, ...newData });
        },
        [product, schemaFields]
    );

    const updateProductFromInput = React.useCallback(
        (field: ProductField, options?: IUpdateOptions) => (event: any) => {
            const target = event.target as HTMLInputElement;
            let value: string | boolean | string[] = target.value;
            if (options?.isMulti) {
                value = value.split(",").map((i) => i.trim());
            }
            if (target.type === "checkbox") {
                value = target.checked;
            }
            if (field === ProductField.Active) {
                value = !value;
            }
            if (field === ProductField.Banners) {
                value = value ? [value as string] : [];
            }
            if (field === ProductField.Description) {
                value = createMarkup(value || "");
            }
            updateProduct([field], [value]);
        },
        [updateProduct]
    );

    const updatePricingField = React.useCallback(
        (currency: string, field: PricingField) => (event: any) => {
            const target = event.target as HTMLInputElement;
            let value: string | boolean = target.value;
            if (target.type === "checkbox") {
                value = target.checked;
            }
            if (field === PricingField.Disabled) {
                value = !value;
            } else {
                const sizes = product[ProductField.Sizes] ?? [];
                sizes.forEach(({ __pricing = [] }) => {
                    __pricing.forEach((priceObj) => {
                        if (priceObj.__currency_code === currency) {
                            priceObj[field] = value;
                        }
                    });
                });
                updateProduct([ProductField.Sizes], [sizes]);
            }
            let pricing = product[ProductField.Pricing] ?? [];
            pricing.forEach((item) => {
                if (item.__currency_code === currency) {
                    item[field] = value;
                }
                if (
                    field !== PricingField.Disabled &&
                    !(item[PricingField.Retail] || item[PricingField.Retail] === 0) &&
                    !(item[PricingField.Wholesale] || item[PricingField.Wholesale] === 0)
                ) {
                    item[PricingField.Disabled] = true;
                }
            });
            // logic to add newly updated values on new currencies
            const existingPriceItem = pricing.filter((item) => {
                return item.__currency_code === currency;
            });
            if (existingPriceItem && existingPriceItem.length === 0) {
                pricing.push({
                    __wholesale: 0,
                    __retail: 0,
                    __disabled: false,
                    __currency_code: currency,
                });
                pricing = pricing.map((item) => {
                    if (item.__currency_code === currency) {
                        item[field] = value;
                    }
                    return item;
                });
            }
            updateProduct([ProductField.Pricing], [pricing]);
        },
        [product, updateProduct]
    );

    const updateSizesField = React.useCallback(
        (idx: number, field: SizesField | string) => (event: any) => {
            const target = event.target as HTMLInputElement;
            let value: string | boolean = target.value;
            if (target.type === "checkbox") {
                value = target.checked;
            }
            const sizes = product[ProductField.Sizes] ?? [];
            const notRequiredFields: string[] = [SizesField.UnitsPerPack, SizesField.ShippingWeight];
            sizes.forEach((item, sizeIdx) => {
                if (idx === sizeIdx) {
                    // Handle not required fields when value it empty
                    if (notRequiredFields.includes(field) && typeof value === "string") {
                        value = value.trim() || undefined;
                    }
                    item[field] = value;
                }
            });
            updateProduct([ProductField.Sizes], [sizes]);
        },
        [product, updateProduct]
    );

    const handleAddSize = React.useCallback(
        (_event: React.MouseEvent) => {
            const sizes = [...(product[ProductField.Sizes] ?? []), { tempId: uuid() }] as unknown as string;
            updateProduct([ProductField.Sizes], [sizes]);
        },
        [product, updateProduct]
    );

    const handleDeleteSize = React.useCallback(
        (idx: number) => (_event: React.MouseEvent) => {
            const sizes = product[ProductField.Sizes] ?? [];
            sizes.splice(idx, 1);
            updateProduct([ProductField.Sizes], [sizes]);
        },
        [product, updateProduct]
    );

    const handleMoveSize = React.useCallback(
        (index: number, newIndex: number) => (_event: React.MouseEvent) => {
            const sizes = product[ProductField.Sizes] ?? [];
            sizes.splice(newIndex, 0, sizes.splice(index, 1)[0]);
            updateProduct([ProductField.Sizes], [sizes]);
        },
        [product, updateProduct]
    );

    React.useEffect(() => {
        const productObj = item.toJS();
        const availableFrom = productObj[ProductField.AvailableFrom];
        const availableTo = productObj[ProductField.AvailableTo];
        if (availableFrom) {
            productObj[ProductField.AvailableFrom] = availableFrom.format("YYYY-MM-DD");
        }
        if (availableTo) {
            productObj[ProductField.AvailableTo] = availableTo.format("YYYY-MM-DD");
        }
        setProduct(cloneDeep(productObj));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!product) {
        return null;
    }

    return (
        <Dialog
            open={true}
            kabob={
                <Kabob
                    productId={product[ProductField.Id]}
                    onEntriesRefresh={onEntriesRefresh}
                    onCloseModal={onCloseModal}
                    onCloseSidePanel={handleCloseSidePanel}
                />
            }
            title={formatMessage("Edit Product")}
            onRequestClose={onCloseModal}
            contentStyle={modalStyles.contentStyle}
            actionsContainerStyle={modalStyles.actionsContainerStyle}
            bodyStyle={modalStyles.body}
            actions={[
                <Button key="cancel" label={formatMessage("Cancel")} onClick={onCloseModal} />,
                <Button
                    key="yes"
                    label={formatMessage("Save Product")}
                    style={modalStyles.btnSave}
                    onClick={handleSave}
                    primary={true}
                    disabled={isSaving}
                />,
            ]}
        >
            <div {...styles}>
                <div className="requiredLabel">* {formatMessage("Required")}</div>
                <FormControl>
                    <Toggle
                        label={formatMessage("Tax exempt")}
                        labelPosition="right"
                        onToggle={updateProductFromInput(ProductField.TaxExempt)}
                        className="toggle"
                        toggled={product[ProductField.TaxExempt]}
                    />
                </FormControl>
                <FormControl>
                    <Toggle
                        label={formatMessage("Disable product")}
                        labelPosition="right"
                        onToggle={updateProductFromInput(ProductField.Active)}
                        className="toggle"
                        toggled={!product[ProductField.Active]}
                    />
                </FormControl>
                {errorWarning && <Alert severity="error">{errorWarning}</Alert>}
                <h3>{formatMessage("Key Information")}</h3>
                <TextField
                    label={`${formatMessage("Style Number")}: *`}
                    fullWidth
                    type="text"
                    onBlur={updateProductFromInput(ProductField.StyleNumber)}
                    defaultValue={product[ProductField.StyleNumber]}
                    variant="outlined"
                    error={!product[ProductField.StyleNumber] || has(errorResponseObj, ProductField.StyleNumber)}
                />
                <TextField
                    label={`${formatMessage("Season")}: *`}
                    fullWidth
                    type="text"
                    onBlur={updateProductFromInput(ProductField.Season)}
                    defaultValue={product[ProductField.Season]}
                    error={!product[ProductField.Season] || has(errorResponseObj, ProductField.Season)}
                    variant="outlined"
                />
                <TextField
                    label={`${formatMessage("Color")}: *`}
                    fullWidth
                    type="text"
                    onBlur={updateProductFromInput(ProductField.Color)}
                    defaultValue={product[ProductField.Color]}
                    error={!product[ProductField.Color] || has(errorResponseObj, ProductField.Color)}
                    variant="outlined"
                />
                <TextField
                    label={`${formatMessage("Name")}: *`}
                    fullWidth
                    type="text"
                    onBlur={updateProductFromInput(ProductField.Name)}
                    defaultValue={product[ProductField.Name]}
                    error={!product[ProductField.Name] || has(errorResponseObj, ProductField.Name)}
                    variant="outlined"
                />
                <TextField
                    label={`${formatMessage("Description")}:`}
                    fullWidth
                    type="text"
                    onBlur={updateProductFromInput(ProductField.Description)}
                    defaultValue={product[ProductField.Description]}
                    variant="outlined"
                    multiline={true}
                    className="productDescription"
                    error={has(errorResponseObj, ProductField.Description)}
                />
                {schemaFields.short_fields.map((field) => {
                    const key = field.key;
                    const type = field.details.type;
                    const allowedValues = field.details.allowed_values ?? [];
                    const value = product[field.key] ?? field.details.default_value ?? "";
                    if (type === "single" && !allowedValues.length) {
                        return (
                            <TextField
                                key={key}
                                label={`${field.name}:${field.required ? " *" : ""}`}
                                fullWidth
                                type="text"
                                onBlur={updateProductFromInput(key)}
                                defaultValue={value}
                                variant="outlined"
                                error={(field.required && !value) || has(errorResponseObj, key)}
                            />
                        );
                    }
                    if (type === "multi" && !allowedValues.length) {
                        const newValue = value && Array.isArray(value) ? value.join(",") : "";
                        return (
                            <TextField
                                key={key}
                                label={`${field.name}:${field.required ? " *" : ""}`}
                                fullWidth
                                type="text"
                                onBlur={updateProductFromInput(key, { isMulti: true })}
                                defaultValue={newValue}
                                variant="outlined"
                                error={
                                    (field.required && !value) ||
                                    has(errorResponseObj, key) ||
                                    checkMultiTypeKeyExists(errorResponseObj, key)
                                }
                            />
                        );
                    }
                    if (type === "single" && allowedValues.length) {
                        return (
                            <FormControl key={key}>
                                <FormLabel className="customLabel">{`${field.name}:${
                                    field.required ? " *" : ""
                                }`}</FormLabel>
                                <Select
                                    key={key}
                                    onChange={updateProductFromInput(key)}
                                    value={value}
                                    variant="outlined"
                                    fullWidth
                                    MenuProps={{ style: modalStyles.selectMenu }}
                                >
                                    {allowedValues.map((allowedValue) => (
                                        <MenuItem key={allowedValue.value} value={allowedValue.value}>
                                            {allowedValue.value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        );
                    }
                    if (type === "multi" && allowedValues.length) {
                        return (
                            <FormControl key={key}>
                                <FormLabel className="customLabel">{`${field.name}:${
                                    field.required ? " *" : ""
                                }`}</FormLabel>
                                <Select
                                    onChange={updateProductFromInput(key)}
                                    value={value}
                                    variant="outlined"
                                    fullWidth
                                    multiple
                                    renderValue={(selected: string[]) => selected.join(", ")}
                                    MenuProps={{ style: modalStyles.selectMenu }}
                                >
                                    {allowedValues.map((allowedValue) => (
                                        <MenuItem key={allowedValue.value} value={allowedValue.value}>
                                            <Checkbox checked={value.includes(allowedValue.value)} />
                                            <ListItemText primary={allowedValue.value} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        );
                    }
                })}
                {schemaFields.long_fields.map((field) => (
                    <TextField
                        key={field.key}
                        label={`${field.name}:${field.required ? " *" : ""}`}
                        fullWidth
                        type="text"
                        onBlur={updateProductFromInput(field.key)}
                        defaultValue={product[field.key] ?? field.details.default_value ?? ""}
                        variant="outlined"
                        multiline={true}
                        className="productDescription"
                        error={
                            (field.required && !(product[field.key] ?? field.details.default_value ?? "")) ||
                            has(errorResponseObj, field.key)
                        }
                    />
                ))}
                <h3>{formatMessage("Availability")}</h3>
                <TextField
                    className="dateField"
                    label={`${formatMessage("Available From Date")}:`}
                    fullWidth
                    type="date"
                    onBlur={updateProductFromInput(ProductField.AvailableFrom)}
                    defaultValue={product[ProductField.AvailableFrom]}
                    variant="outlined"
                    error={has(errorResponseObj, ProductField.AvailableFrom)}
                />
                <TextField
                    className="dateField"
                    label={`${formatMessage("Available To Date")}:`}
                    fullWidth
                    type="date"
                    onBlur={updateProductFromInput(ProductField.AvailableTo)}
                    defaultValue={product[ProductField.AvailableTo]}
                    variant="outlined"
                    error={has(errorResponseObj, ProductField.AvailableTo)}
                />
                {schemaFields.date_fields.map((field) => (
                    <TextField
                        className="dateField"
                        key={field.key}
                        label={`${field.name}:${field.required ? " *" : ""}`}
                        fullWidth
                        type="date"
                        onBlur={updateProductFromInput(field.key)}
                        defaultValue={moment
                            .utc(product[field.key] ?? field.details.default_value ?? "")
                            .format("YYYY-MM-DD")}
                        variant="outlined"
                        error={has(errorResponseObj, field.key)}
                    />
                ))}
                <h3>{formatMessage("Pricing")}</h3>
                {!productIsPricePerSize ? (
                    currencies.map((currency) => (
                        <div key={currency.currency_code}>
                            <FormControl className="pricingToggle">
                                <Toggle
                                    label={currency.currency_code}
                                    labelPosition="right"
                                    onToggle={updatePricingField(currency.currency_code, PricingField.Disabled)}
                                    className="toggle"
                                    toggled={!currency.disabled}
                                />
                            </FormControl>
                            <TextField
                                label={`${formatMessage("Wholesale")}:`}
                                fullWidth
                                type="text"
                                onBlur={updatePricingField(currency.currency_code, PricingField.Wholesale)}
                                defaultValue={currency.wholesale ?? ""}
                                error={isNaN(+currency.wholesale)}
                                variant="outlined"
                            />
                            <TextField
                                label={`${formatMessage("MSRP")}:`}
                                fullWidth
                                type="text"
                                onBlur={updatePricingField(currency.currency_code, PricingField.Retail)}
                                defaultValue={currency.retail ?? ""}
                                error={isNaN(+currency.retail)}
                                variant="outlined"
                            />
                        </div>
                    ))
                ) : (
                    <div>
                        {formatMessage("This product uses price per size, which must be edited with a csv/xls/xlsx.")}
                    </div>
                )}
                {scope.brand.getPreference("use_pricing_templates") &&
                    !!product[ProductField.PriceTemplates]?.length && (
                        <>
                            <h3>{formatMessage("Price Sheets")}</h3>
                            <Grid container spacing={3}>
                                <Grid item xs={3}>
                                    {formatMessage("Price Sheet")}
                                </Grid>
                                <Grid item xs={3}>
                                    {formatMessage("Wholesale Price")}
                                </Grid>
                                <Grid item xs={3}>
                                    {formatMessage("M.S.R.P.")}
                                </Grid>
                                <Grid item xs={3}>
                                    {formatMessage("Disabled")}
                                </Grid>
                                {product[ProductField.PriceTemplates].map((template) => (
                                    <>
                                        <Grid item xs={3}>
                                            {template.__template}
                                        </Grid>
                                        <Grid item xs={3}>
                                            {template.__wholesale || "-"}
                                        </Grid>
                                        <Grid item xs={3}>
                                            {template.__retail || "-"}
                                        </Grid>
                                        <Grid item xs={3}>
                                            {formatMessage(template.__disabled ? "Yes" : "No")}
                                        </Grid>
                                    </>
                                ))}
                            </Grid>
                        </>
                    )}
                <h3>{formatMessage("Banner")}</h3>
                {banners.length ? (
                    <Grid container spacing={3}>
                        <Grid item lg={3}>
                            <Select
                                onChange={updateProductFromInput(ProductField.Banners)}
                                value={product[ProductField.Banners]?.[0] ?? ""}
                                variant="outlined"
                                fullWidth
                                MenuProps={{ style: modalStyles.selectMenu }}
                            >
                                <MenuItem value="">{formatMessage("None")}</MenuItem>
                                {banners.map((banner) => (
                                    <MenuItem key={banner._id} value={banner._id}>
                                        {banner.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Grid>
                    </Grid>
                ) : (
                    <div>{formatMessage("Go to Admin > Product Banners to create product banners.")}</div>
                )}
                <h3>{formatMessage("Sizes")}</h3>
                {product[ProductField.Sizes].map((size, idx) => {
                    const sid = size._id ?? size.tempId;
                    let sizeValue;
                    if (size.__size_group) {
                        sizeValue = size.__size.replace(new RegExp(escapeRegExp(size.__size_group) + "$"), "");
                    } else {
                        sizeValue = size.__size;
                    }
                    return (
                        <SizeRow
                            key={sid}
                            id={sid}
                            index={idx}
                            onMove={handleMoveSize}
                            updateSizesField={updateSizesField}
                            sizeValue={sizeValue}
                            unitsPerPackValue={size.__units_per_pack}
                            shippingWeightValue={size.__weight}
                            schemaFields={schemaFields}
                            size={size}
                            handleDeleteSize={handleDeleteSize}
                            length={product[ProductField.Sizes]?.length ?? 0}
                            sizeGroup={size.__size_group}
                            errorResponseObj={errorResponseObj}
                        />
                    );
                })}
                {(!product.__size_groups || !product.__size_groups.length) && (
                    <div className="addSize">
                        <MUIButton onClick={handleAddSize}>
                            <Add />
                            {formatMessage("Add another size")}
                        </MUIButton>
                    </div>
                )}
                <h3>{formatMessage("Size Packs")}</h3>
                <SizePacks productId={product[ProductField.Id]} sizePacksIds={product[ProductField.sizePacks] ?? []} />
            </div>
        </Dialog>
    );
};

export default withRouter(ProductEdit);
